import React from 'react';
import PropTypes from "prop-types";
import {useFormik} from "formik";

import {ResponsiveDialog} from "../../../../../components/ResponsiveDialog";
import GuestItem from "../GuestItem";
import getValidationSchema from "./validator";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import MenuItem from "@mui/material/MenuItem";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import {OptionalContent} from "../../../../../components/OptionalContent";
import {useFeedbackTools} from "../../../../../components/FeedbackTools";
import {useCheckOutCreate} from "../../../query";


const FormCheckOut = props => {
    const {booking, onClose} = props;
    const {showSuccessNotification, showErrorNotification} = useFeedbackTools();
    const checkOutCreate = useCheckOutCreate({bookingId: booking.id});

    const guestStays = React.useMemo(
        () => booking.getNotCheckedOutGuestStays(),
        [booking]); // eslint-disable-line react-hooks/exhaustive-deps

    const allowedDates = React.useMemo(
        () => booking.getAllowedDepartureDates(),
        []); // eslint-disable-line react-hooks/exhaustive-deps

    const formik = useFormik({
        initialValues: {
            checkOutDate: allowedDates.length > 0 ? allowedDates[0].value : "",
            guestStayIds: guestStays.map(s => s.id),
        },
        validationSchema: getValidationSchema(guestStays),
        validateOnMount: true,
        onSubmit: async values => {
            try {
                await checkOutCreate.mutateAsync({data: values});
                showSuccessNotification("Check-Out creato con successo");
                onClose();
            } catch (e) {
                console.error(e);
                showErrorNotification(e);
            }
        }
    });

    const handleToggleGuestStay= (guestStayIds) => () => {
        if (formik.values.guestStayIds.includes(guestStayIds)) {
            formik.setFieldValue("guestStayIds", formik.values.guestStayIds.filter(v => v !== guestStayIds))
        } else {
            formik.setFieldValue("guestStayIds", [...formik.values.guestStayIds, guestStayIds])
        }
    }

    const submitIsEnabled = !Object.keys(formik.errors).some(x => formik.touched[x]);

    return (
        <ResponsiveDialog title="Check Out"
                          isOpen
                          maxWidth="md"
                          scroll="paper"
                          isLoading={checkOutCreate.isLoading}
                          showApply={true}
                          applyLabel="Salva"
                          enableApply={submitIsEnabled}
                          onApply={formik.handleSubmit}
                          closeLabel="Cancella"
                          onClose={onClose}>

            {
                formik.touched.guestStayIds && Boolean(formik.errors.guestStayIds) &&
                <Alert severity="error" sx={{mb: 3}}>
                    <AlertTitle>Attenzione</AlertTitle>
                    {formik.errors.guestStayIds}
                </Alert>
            }

            <TextField
                select
                label="Data di partenza"
                name="checkOutDate"
                fullWidth
                value={formik.values.checkOutDate}
                onChange={formik.handleChange}
                error={formik.touched.checkOutDate && Boolean(formik.errors.checkOutDate)}
                helperText={formik.touched.checkOutDate && formik.errors.checkOutDate}
                sx={{mt: 1}}>
                {
                    allowedDates.map((d, i) => (<MenuItem key={i} value={d.value}>{d.label}</MenuItem>))
                }
            </TextField>

            <Typography variant="h6" sx={{color: 'primary.main', mt: 4, mb: 1}}>
                Ospiti
            </Typography>

            <OptionalContent hasContent={guestStays.length !== 0} showIcon={true} noContentMessage="Nessun ospite pronto per il check out">
                <List disablePadding sx={{mt: 1}}>
                    {guestStays.map((g, i) => (
                        <ListItem
                            key={i}
                            disablePadding
                            divider={i !== guestStays.length - 1}>
                            <ListItemIcon sx={{minWidth: 40}}>
                                <Checkbox onChange={handleToggleGuestStay(g.id)}
                                          checked={formik.values.guestStayIds.includes(g.id)}
                                          edge="start"/>
                            </ListItemIcon>
                            <GuestItem guest={g.guestDocument} showStatus={false} disableHorizontalPadding={true}/>
                        </ListItem>
                    ))}
                </List>
            </OptionalContent>
        </ResponsiveDialog>
    );
};

FormCheckOut.propTypes = {
    booking: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
}

export default FormCheckOut;